import { uploadSessionRepository } from '~/repositories/uploadSessionRepository';
import { validateId } from '~/repositories/utils';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type {
  AddUnitListingImageItemDto,
  HydratedUnitListingDto,
  ImportUnitListingDto,
  PaginatedUnitListingDto,
  PropertyType,
  UnitListingApplicationInviteDto,
  UnitListingSyndicationStatusDto,
  UnitMasterListingDto,
  UnitMasterListingRequestDto,
  UpdateUnitListingDto,
  UpdateUnitListingImageItemDto,
  UpdateUnitListingImagesDto,
} from '~/swagger/portal/Api';

const UNIT_LISTINGS_URL = '/api/unit-listings';

export type UnitListingFilter = {
  page: number;
  pageSize: number;
  portfolioId?: string;
  propertyId?: string;
  listed?: boolean;
  pinned?: boolean;
  availableFrom?: string | undefined;
  minRentalPrice?: number;
  maxRentalPrice?: number;
  minBeds?: number;
  minBaths?: number;
  propertyType?: PropertyType;
  search?: string | undefined;
};
export type UnitListingImagePayload = {
  file: File;
  description?: string;
};
export type addUnitListingPayload = {
  portfolioIds?: string[] | undefined;
  propertyIds?: string[] | undefined;
  unitIds?: string[] | undefined;
};

export class UnitListingsRepository extends BaseRestRepository {
  public async getUnitListings(filter: UnitListingFilter): Promise<PaginatedUnitListingDto> {
    const url = urlWithQuery(UNIT_LISTINGS_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async uploadUnitListingImages(unitId: string, files: File[]): Promise<string[]> {
    validateId(unitId, 'UnitListing');
    return uploadSessionRepository.uploadFiles(`${UNIT_LISTINGS_URL}/${unitId}/images/upload-sessions`, files);
  }

  public async addUnitListingImages(unitId: string, items: AddUnitListingImageItemDto[]): Promise<void> {
    validateId(unitId, 'UnitListing');
    await this.fetchWithAuth(`${UNIT_LISTINGS_URL}/${unitId}/images`, {
      method: 'POST',
      body: JSON.stringify({ images: items }),
    });
  }

  public async updateUnitListingImages(unitId: string, items: UpdateUnitListingImageItemDto[] | UpdateUnitListingImagesDto): Promise<void> {
    validateId(unitId, 'UnitListing');
    const model = Array.isArray(items) ? { images: items } : items;
    await this.fetchWithAuth(`${UNIT_LISTINGS_URL}/${unitId}/images`, {
      method: 'PUT',
      body: JSON.stringify(model),
    });
  }

  public async getUnitListingDetail(unitListingId: string): Promise<HydratedUnitListingDto> {
    const url = `${UNIT_LISTINGS_URL}/${unitListingId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async removeUnitListingImage(unitId: string, imageId: string): Promise<void> {
    validateId(unitId, 'UnitListing');
    validateId(imageId, 'Image');
    await this.fetchWithAuth(`${UNIT_LISTINGS_URL}/${unitId}/images/${imageId}`, { method: 'DELETE' });
  }

  public async deleteUnitListingImage(unitId: string, imageId: string): Promise<void> {
    await this.removeUnitListingImage(unitId, imageId);
  }

  public async addUnitListing(payload: addUnitListingPayload): Promise<ImportUnitListingDto> {
    const url = `${UNIT_LISTINGS_URL}/import`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }
  public async generateAiListing(unitId: string, prompt: UnitMasterListingRequestDto): Promise<UnitMasterListingDto> {
    const url = `${UNIT_LISTINGS_URL}/${unitId}/generate-ai-masterlisting`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(prompt),
    });
    return this.getJsonResponse(response);
  }
  public async updateUnitListing(model: UpdateUnitListingDto, unitId: string): Promise<UpdateUnitListingDto> {
    const url = `${UNIT_LISTINGS_URL}/${unitId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }
  public async deactivateUnitListing(unitId: string): Promise<void> {
    const url = `${UNIT_LISTINGS_URL}/${unitId}/deactivate`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async getUnitListingSyndicationStatus(unitListingId: string): Promise<UnitListingSyndicationStatusDto> {
    const url = `${UNIT_LISTINGS_URL}/${unitListingId}/syndication-status`;
    const response = await this.fetchWithAuth(url, {}, false);
    return this.getJsonResponse(response);
  }

  public async applicationInvite(unitId: string, payload: UnitListingApplicationInviteDto): Promise<void> {
    const url = `${UNIT_LISTINGS_URL}/${unitId}/application-invite`;
    await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

export const unitListingsRepository = new UnitListingsRepository();
